import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Paper,
  Pill,
  Stack,
  Table,
  Text,
  Title,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import type { ThreatActorProfile } from '../../types';
import {
  activityColor,
  arsenalTypeColor,
  arsenalTypeLabel,
  formatActorDate,
  formatCurrency,
  formatMotivation,
  formatResourceLevel,
  motivationColor,
  referenceDomain,
  REVENUE_BAND_LABELS,
  severityColor,
} from './threat-actor-format';

const ALIAS_LIMIT = 5;
const TECH_LIMIT = 10;
const REF_LIMIT = 5;

function SectionHeader({
  children,
  right,
}: Readonly<{ children: React.ReactNode; right?: React.ReactNode }>) {
  return (
    <Group justify="space-between" mb="sm">
      <Text size="sm" fw={600} tt="uppercase" c="dimmed">
        {children}
      </Text>
      {right}
    </Group>
  );
}

function MiniBar({
  value,
  color = 'blue',
}: Readonly<{ value: number; color?: string }>) {
  const pct = Math.round(value * 100);
  return (
    <Group gap="xs" style={{ minWidth: 120 }}>
      <Box
        style={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          background: 'var(--mantine-color-default-border)',
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 4,
            background: `var(--mantine-color-${color}-6)`,
          }}
        />
      </Box>
      <Text size="xs" fw={600} style={{ width: 36, textAlign: 'right' }}>
        {pct}%
      </Text>
    </Group>
  );
}

function InfoRow({
  label,
  value,
  children,
}: Readonly<{
  label: string;
  value?: string;
  children?: React.ReactNode;
}>) {
  return (
    <Group
      justify="space-between"
      py={6}
      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
    >
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      {children || (
        <Text size="sm" fw={500} ta="right" style={{ maxWidth: '60%' }}>
          {value || '-'}
        </Text>
      )}
    </Group>
  );
}

const linkBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
} as const;

const rowBorder = {
  borderBottom: '1px solid var(--mantine-color-gray-2)',
} as const;

export default function ThreatActorDetailBody({
  profile,
}: Readonly<{ profile: ThreatActorProfile }>) {
  const [descExpanded, setDescExpanded] = useState(false);
  const [aliasesExpanded, setAliasesExpanded] = useState(false);
  const [techniquesExpanded, setTechniquesExpanded] = useState(false);
  const [refsExpanded, setRefsExpanded] = useState(false);

  const visibleAliases = aliasesExpanded
    ? profile.aliases
    : profile.aliases.slice(0, ALIAS_LIMIT);
  const visibleTechniques = techniquesExpanded
    ? profile.associatedTechniques
    : profile.associatedTechniques.slice(0, TECH_LIMIT);
  const visibleRefs = refsExpanded
    ? profile.references
    : profile.references.slice(0, REF_LIMIT);

  return (
    <Stack gap={0}>
      {/* Section 1: Identity Banner */}
      <Box
        pb="md"
        mb="md"
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={3}>{profile.name}</Title>
            <Text size="sm" c="dimmed">
              {profile.mitreId}
            </Text>
          </Box>
          {profile.mitreUrl && (
            <ActionIcon
              variant="subtle"
              component="a"
              href={profile.mitreUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open MITRE ATT&CK profile"
              title="Open MITRE ATT&CK profile"
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </ActionIcon>
          )}
        </Group>

        <Group gap="xs" mt="sm">
          {profile.activityLevel && (
            <Badge
              size="sm"
              variant="dot"
              color={activityColor(profile.activityLevel)}
            >
              {profile.activityLevel}
            </Badge>
          )}
          <Badge
            size="sm"
            variant="light"
            color={motivationColor(profile.primaryMotivation)}
          >
            {formatMotivation(profile.primaryMotivation)}
          </Badge>
          <Badge size="sm" variant="outline" color="gray">
            {formatResourceLevel(profile.resourceLevel)}
          </Badge>
        </Group>

        {profile.aliases.length > 0 && (
          <Group gap={4} mt="sm" wrap="wrap">
            <Text size="xs" c="dimmed" mr={4}>
              Also known as:
            </Text>
            {visibleAliases.map((alias) => (
              <Pill key={alias} size="sm">
                {alias}
              </Pill>
            ))}
            {profile.aliases.length > ALIAS_LIMIT && (
              <Text
                component="button"
                size="xs"
                c="blue"
                onClick={() => setAliasesExpanded((v) => !v)}
                style={linkBtn}
              >
                {aliasesExpanded
                  ? 'show fewer'
                  : `+${profile.aliases.length - ALIAS_LIMIT} more`}
              </Text>
            )}
          </Group>
        )}
      </Box>

      {/* Section 2: Overview */}
      {(profile.description || profile.firstSeen || profile.lastSeen) && (
        <Box
          pb="md"
          mb="md"
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <SectionHeader>Overview</SectionHeader>
          {profile.description && (
            <>
              <Text size="sm" lh={1.6} lineClamp={descExpanded ? undefined : 4}>
                {profile.description}
              </Text>
              {profile.description.length > 300 && (
                <Text
                  component="button"
                  size="xs"
                  c="blue"
                  mt={4}
                  onClick={() => setDescExpanded((v) => !v)}
                  style={linkBtn}
                >
                  {descExpanded ? 'show less' : 'show more'}
                </Text>
              )}
            </>
          )}
          {(profile.firstSeen || profile.lastSeen) && (
            <Group gap="xl" mt="md">
              <Stack gap={0}>
                <Text size="xs" c="dimmed">
                  Active Since
                </Text>
                <Text size="sm" fw={500}>
                  {formatActorDate(profile.firstSeen)}
                </Text>
              </Stack>
              <Stack gap={0}>
                <Text size="xs" c="dimmed">
                  Last Known Activity
                </Text>
                <Text size="sm" fw={500}>
                  {formatActorDate(profile.lastSeen)}
                </Text>
              </Stack>
            </Group>
          )}
        </Box>
      )}

      {/* Section 3: Claims Impact */}
      {profile.claimsImpact && (
        <Box
          pb="md"
          mb="md"
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <SectionHeader>Claims Impact</SectionHeader>

          {profile.claimsImpact.incidentMatches.length > 0 ? (
            <Group gap="xs" mb="md">
              {profile.claimsImpact.incidentMatches.map((m) => (
                <Badge key={m.type} size="sm" variant="light" color="blue">
                  {m.type} ({m.overlapCount} TTPs)
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed" fs="italic" mb="md">
              No incident profile match — actor may use novel techniques
            </Text>
          )}

          {(profile.claimsImpact.resolution != null ||
            profile.claimsImpact.ransomwareIntel?.doubleExtortionProbability !=
              null) && (
            <Group gap="md" mb="md">
              {profile.claimsImpact.resolution != null && (
                <>
                  <Paper
                    withBorder
                    p="md"
                    style={{
                      flex: 1,
                      borderLeft: '3px solid var(--mantine-color-blue-6)',
                    }}
                  >
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Avg Resolution
                    </Text>
                    <Text size="xl" fw={700}>
                      {profile.claimsImpact.resolution.avgResolutionDays} days
                    </Text>
                  </Paper>
                  <Paper
                    withBorder
                    p="md"
                    style={{
                      flex: 1,
                      borderLeft: '3px solid var(--mantine-color-teal-6)',
                    }}
                  >
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Median Incurred
                    </Text>
                    <Text size="xl" fw={700}>
                      {formatCurrency(
                        profile.claimsImpact.resolution.medianTotalIncurred
                      )}
                    </Text>
                  </Paper>
                </>
              )}
              {profile.claimsImpact.ransomwareIntel
                ?.doubleExtortionProbability != null && (
                <Paper
                  withBorder
                  p="md"
                  style={{
                    flex: 1,
                    borderLeft: '3px solid var(--mantine-color-red-6)',
                  }}
                >
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Double Extortion
                  </Text>
                  <Text size="xl" fw={700}>
                    {Math.round(
                      profile.claimsImpact.ransomwareIntel
                        .doubleExtortionProbability * 100
                    )}
                    %
                  </Text>
                </Paper>
              )}
            </Group>
          )}

          {profile.claimsImpact.ransomwareIntel && (
            <>
              <Text
                size="xs"
                fw={600}
                tt="uppercase"
                c="dimmed"
                mt="lg"
                mb="sm"
              >
                Ransom Negotiation Intelligence
              </Text>

              {profile.claimsImpact.ransomwareIntel.avgDemandByRevenueBand && (
                <Table striped highlightOnHover mb="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <Text size="xs" fw={600}>
                          Insured Revenue
                        </Text>
                      </Table.Th>
                      <Table.Th>
                        <Text size="xs" fw={600}>
                          Typical Demand Range
                        </Text>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {Object.entries(
                      profile.claimsImpact.ransomwareIntel
                        .avgDemandByRevenueBand
                    ).map(([band, range]) => (
                      <Table.Tr key={band}>
                        <Table.Td>
                          <Text size="sm">
                            {REVENUE_BAND_LABELS[band] ?? band}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={600}>
                            {formatCurrency(range.min)} –{' '}
                            {formatCurrency(range.max)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}

              <Stack gap={0}>
                {profile.claimsImpact.ransomwareIntel.negotiationSuccessRate !=
                  null && (
                  <InfoRow label="Negotiation Success Rate">
                    <MiniBar
                      value={
                        profile.claimsImpact.ransomwareIntel
                          .negotiationSuccessRate
                      }
                      color="green"
                    />
                  </InfoRow>
                )}
                {profile.claimsImpact.ransomwareIntel
                  .avgDiscountFromInitialDemand != null && (
                  <InfoRow label="Avg Discount from Ask">
                    <MiniBar
                      value={
                        profile.claimsImpact.ransomwareIntel
                          .avgDiscountFromInitialDemand
                      }
                      color="blue"
                    />
                  </InfoRow>
                )}
                {profile.claimsImpact.ransomwareIntel.dataLeakTimelineDays !=
                  null && (
                  <InfoRow
                    label="Data Leak Timeline"
                    value={`${profile.claimsImpact.ransomwareIntel.dataLeakTimelineDays.min}–${profile.claimsImpact.ransomwareIntel.dataLeakTimelineDays.max} days (median: ${profile.claimsImpact.ransomwareIntel.dataLeakTimelineDays.median})`}
                  />
                )}
                {profile.claimsImpact.ransomwareIntel.avgDwellTimeDays !=
                  null && (
                  <InfoRow
                    label="Avg Dwell Time"
                    value={`${profile.claimsImpact.ransomwareIntel.avgDwellTimeDays.min}–${profile.claimsImpact.ransomwareIntel.avgDwellTimeDays.max} days (median: ${profile.claimsImpact.ransomwareIntel.avgDwellTimeDays.median})`}
                  />
                )}
                {profile.claimsImpact.ransomwareIntel.bluffProbability !=
                  null && (
                  <InfoRow
                    label="Bluff Probability"
                    value={`${Math.round(
                      profile.claimsImpact.ransomwareIntel.bluffProbability *
                        100
                    )}%`}
                  />
                )}
              </Stack>
            </>
          )}

          {profile.claimsImpact.expectedEscalation.length > 0 && (
            <>
              <Text
                size="xs"
                fw={600}
                tt="uppercase"
                c="dimmed"
                mt="lg"
                mb="sm"
              >
                Expected Escalation
              </Text>
              <Paper
                p="sm"
                radius="sm"
                style={{ background: 'var(--mantine-color-default-hover)' }}
              >
                <Stack gap="xs">
                  {profile.claimsImpact.expectedEscalation.map((item, i) => (
                    <Text key={item} size="sm" lh={1.5}>
                      {i + 1}. {item}
                    </Text>
                  ))}
                </Stack>
              </Paper>
            </>
          )}
        </Box>
      )}

      {/* Section 4: Attack Playbook */}
      {profile.associatedTechniques.length > 0 && (
        <Box
          pb="md"
          mb="md"
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <SectionHeader
            right={
              <Badge
                size="sm"
                variant="filled"
                color={severityColor(profile.techniqueCount)}
              >
                {profile.techniqueCount} techniques
              </Badge>
            }
          >
            Attack Playbook
          </SectionHeader>
          <Stack gap={0}>
            {visibleTechniques.map((tech) => (
              <Group
                key={tech.tid}
                justify="space-between"
                py={6}
                style={rowBorder}
              >
                <Group gap="sm">
                  <Text
                    size="xs"
                    ff="monospace"
                    c="blue"
                    style={{ minWidth: 76 }}
                  >
                    {tech.tid}
                  </Text>
                  <Text size="sm">{tech.name}</Text>
                </Group>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  component="a"
                  href={`https://attack.mitre.org/techniques/${tech.tid.replaceAll(
                    '.',
                    '/'
                  )}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${tech.tid} on MITRE`}
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
          {profile.associatedTechniques.length > TECH_LIMIT && (
            <Text
              component="button"
              size="xs"
              c="blue"
              mt="xs"
              onClick={() => setTechniquesExpanded((v) => !v)}
              style={linkBtn}
            >
              {techniquesExpanded
                ? 'Show fewer'
                : `Show all ${profile.associatedTechniques.length} techniques`}
            </Text>
          )}
        </Box>
      )}

      {/* Section 5: Known Arsenal */}
      {profile.arsenal.length > 0 && (
        <Box
          pb="md"
          mb="md"
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <SectionHeader
            right={
              <Badge size="sm" variant="light" color="gray">
                {profile.arsenal.length} tools
              </Badge>
            }
          >
            Known Arsenal
          </SectionHeader>
          <Stack gap={0}>
            {[...profile.arsenal]
              .sort((a, b) => Number(b.isRansomware) - Number(a.isRansomware))
              .map((sw) => (
                <Group
                  key={sw.name}
                  justify="space-between"
                  py={6}
                  style={{
                    ...rowBorder,
                    ...(sw.isRansomware
                      ? {
                          borderLeft: '2px solid var(--mantine-color-red-5)',
                          paddingLeft: 'var(--mantine-spacing-xs)',
                        }
                      : {}),
                  }}
                >
                  <Group gap="sm">
                    <Text size="sm" fw={500}>
                      {sw.name}
                    </Text>
                    <Badge
                      size="xs"
                      variant="light"
                      color={arsenalTypeColor(sw)}
                    >
                      {arsenalTypeLabel(sw)}
                    </Badge>
                  </Group>
                  <Group gap="sm">
                    {sw.platforms.length > 0 && (
                      <Text size="xs" c="dimmed">
                        {sw.platforms.join(', ')}
                      </Text>
                    )}
                    <Text size="xs" fw={600}>
                      {sw.techniqueCount} TTPs
                    </Text>
                  </Group>
                </Group>
              ))}
          </Stack>
        </Box>
      )}

      {/* Section 6: References */}
      {profile.references.length > 0 && (
        <Box pb="md">
          <SectionHeader>References</SectionHeader>
          <Stack gap={0}>
            {visibleRefs.map((ref) => (
              <Group
                key={ref.url}
                justify="space-between"
                py={6}
                style={rowBorder}
              >
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    component="a"
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    c="blue"
                    fw={500}
                    lineClamp={1}
                  >
                    {referenceDomain(ref.url)}
                  </Text>
                </Box>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  component="a"
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open reference"
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
          {profile.references.length > REF_LIMIT && (
            <Text
              component="button"
              size="xs"
              c="blue"
              mt="xs"
              onClick={() => setRefsExpanded((v) => !v)}
              style={linkBtn}
            >
              {refsExpanded
                ? 'Show fewer'
                : `Show all ${profile.references.length}`}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
}
